import { Component, EventEmitter, Input, Output, forwardRef, ElementRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-custom-date-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './custom-date-picker.component.html',
  styleUrl: './custom-date-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDatePickerComponent),
      multi: true
    }
  ]
})
export class CustomDatePickerComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input() placeholder: string = 'dd/mm/aaaa';
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() minDate: string = '';
  @Input() maxDate: string = '';
  @Input() blockedUntilDate: string = ''; // Nueva propiedad para bloquear fechas hasta una fecha específica
  @Output() dateChange = new EventEmitter<string>();

  @ViewChild('calendarModal') calendarModal!: ElementRef;

  value: string = '';
  showCalendar: boolean = false;
  currentDate: Date = new Date();
  selectedDate: Date | null = null;
  
  // Días de la semana
  weekDays: string[] = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
  
  // Meses del año
  months: string[] = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // ControlValueAccessor implementation
  private onChange = (value: string) => {};
  private onTouched = () => {};

  writeValue(value: string): void {
    this.value = value || '';
    if (value) {
      this.selectedDate = new Date(value);
      this.currentDate = new Date(value);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngAfterViewInit(): void {
    // Implementación opcional para ajustar posición del dropdown
    // Agregar listeners para ajustar posición en scroll y resize
    window.addEventListener('scroll', this.handleScrollOrResize.bind(this), true);
    window.addEventListener('resize', this.handleScrollOrResize.bind(this));
  }

  ngOnDestroy(): void {
    // Limpiar listeners
    window.removeEventListener('scroll', this.handleScrollOrResize.bind(this), true);
    window.removeEventListener('resize', this.handleScrollOrResize.bind(this));
  }

  private handleScrollOrResize(): void {
    if (this.showCalendar && this.calendarModal) {
      this.adjustDropdownPosition();
    }
  }

  // Abrir/cerrar calendario
  toggleCalendar(): void {
    if (!this.disabled) {
      this.showCalendar = !this.showCalendar;
      if (!this.showCalendar) {
        this.onTouched();
      } else {
        // Ajustar posición del dropdown después de que se muestre
        setTimeout(() => this.adjustDropdownPosition(), 0);
      }
    }
  }

  // Ajustar posición del dropdown si se sale de la pantalla
  private adjustDropdownPosition(): void {
    if (!this.calendarModal) return;

    const modal = this.calendarModal.nativeElement;
    const inputElement = modal.closest('.custom-date-picker').querySelector('.date-input-wrapper');
    const inputRect = inputElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    
    // Limpiar clases de posicionamiento previas
    modal.classList.remove('dropdown-up', 'dropdown-right');
    
    // Calcular posición horizontal (centrado respecto al input)
    let leftPosition = inputRect.left + (inputRect.width / 2) - (modal.offsetWidth / 2);
    leftPosition = Math.max(10, Math.min(leftPosition, windowWidth - modal.offsetWidth - 10));
    
    // Calcular posición vertical (debajo del input por defecto)
    let topPosition = inputRect.bottom + 4;
    let isAbove = false;
    
    // Si no cabe debajo, mostrarlo arriba
    if (topPosition + modal.offsetHeight > windowHeight - 20) {
      topPosition = inputRect.top - modal.offsetHeight - 4;
      isAbove = true;
      modal.classList.add('dropdown-up');
    }
    
    // Si no cabe arriba ni abajo, centrar verticalmente
    if (topPosition < 10) {
      topPosition = Math.max(10, (windowHeight - modal.offsetHeight) / 2);
    }
    
    // Aplicar posición fija
    modal.style.position = 'fixed';
    modal.style.top = `${topPosition}px`;
    modal.style.left = `${leftPosition}px`;
    modal.style.right = 'auto';
    modal.style.bottom = 'auto';
    
    // Si se ajustó horizontalmente, agregar clase
    if (leftPosition !== inputRect.left + (inputRect.width / 2) - (modal.offsetWidth / 2)) {
      modal.classList.add('dropdown-right');
    }
  }

  closeCalendar(): void {
    this.showCalendar = false;
    this.onTouched();
  }

  // Navegación de meses
  previousMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
  }

  nextMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
  }

  // Obtener días del mes actual
  getDaysInMonth(): (number | null)[] {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();
    
    // Primer día del mes
    const firstDay = new Date(year, month, 1);
    // Último día del mes
    const lastDay = new Date(year, month + 1, 0);
    
    // Día de la semana del primer día (0=domingo, 1=lunes, etc.)
    let startWeekDay = firstDay.getDay();
    // Convertir domingo (0) a 7 para que lunes sea 1
    startWeekDay = startWeekDay === 0 ? 7 : startWeekDay;
    
    const daysInMonth = lastDay.getDate();
    const days: (number | null)[] = [];
    
    // Días vacíos al inicio (días del mes anterior)
    const daysFromPrevMonth = startWeekDay - 1;
    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    
    for (let i = daysFromPrevMonth; i > 0; i--) {
      days.push(null); // Usar null para días del mes anterior
    }
    
    // Días del mes actual
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    // Completar con días del próximo mes si es necesario
    const totalCells = 42; // 6 semanas × 7 días
    while (days.length < totalCells) {
      days.push(null);
    }
    
    return days;
  }

  // Verificar si un día está seleccionado
  isDaySelected(day: number | null): boolean {
    if (!day || !this.selectedDate) return false;
    
    const currentMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), day);
    return (
      currentMonth.getFullYear() === this.selectedDate.getFullYear() &&
      currentMonth.getMonth() === this.selectedDate.getMonth() &&
      currentMonth.getDate() === this.selectedDate.getDate()
    );
  }

  // Verificar si un día es hoy
  isToday(day: number | null): boolean {
    if (!day) return false;
    
    const today = new Date();
    const currentMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), day);
    
    return (
      currentMonth.getFullYear() === today.getFullYear() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getDate() === today.getDate()
    );
  }

  // Verificar si un día está deshabilitado por límites de fecha
  isDayDisabled(day: number | null): boolean {
    if (!day) return false;
    
    const currentMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), day);
    
    // Verificar fechas bloqueadas hasta una fecha específica (inclusive)
    if (this.blockedUntilDate) {
      // Crear fecha sin problemas de zona horaria
      const blockedDateParts = this.blockedUntilDate.split('-');
      const blockedDate = new Date(parseInt(blockedDateParts[0]), parseInt(blockedDateParts[1]) - 1, parseInt(blockedDateParts[2]));
      blockedDate.setHours(0, 0, 0, 0); // Incluir todo el día bloqueado
      currentMonth.setHours(0, 0, 0, 0);
      
      if (currentMonth <= blockedDate) {
        return true;
      }
    }
    
    // Verificar límites de fecha mínima (inclusive: fechas ANTES de minDate están deshabilitadas, minDate está habilitada)
    if (this.minDate) {
      // Crear fecha sin problemas de zona horaria
      const minDateParts = this.minDate.split('-');
      const minDate = new Date(parseInt(minDateParts[0]), parseInt(minDateParts[1]) - 1, parseInt(minDateParts[2]));
      // Normalizar horas para comparación solo de fechas
      minDate.setHours(0, 0, 0, 0);
      currentMonth.setHours(0, 0, 0, 0);

      console.log('🔍 currentMonth:', currentMonth);
      console.log('🔍 minDate:', minDate);
      console.log('🔍 currentMonth < minDate:', currentMonth < minDate);
      // Deshabilitar solo fechas ANTERIORES a la fecha mínima (la fecha mínima debe estar habilitada)
      if (currentMonth < minDate) {
        return true;
      }
    }
    
  // Verificar límites de fecha máxima (exclusive: fechas DESPUÉS de maxDate están deshabilitadas)
  if (this.maxDate) {
    // Crear fecha sin problemas de zona horaria
    const maxDateParts = this.maxDate.split('-');
    const maxDate = new Date(parseInt(maxDateParts[0]), parseInt(maxDateParts[1]) - 1, parseInt(maxDateParts[2]));
    // Normalizar horas para comparación solo de fechas
    maxDate.setHours(0, 0, 0, 0);
    const currentDateCheck = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), day);
    currentDateCheck.setHours(0, 0, 0, 0);
      if (currentDateCheck > maxDate) {
        return true;
      }
  }
    
    return false;
  }

  // Seleccionar un día
  selectDay(day: number | null): void {
    if (!day) return;
    
    // No permitir selección de días deshabilitados
    if (this.isDayDisabled(day)) return;
    
    const selectedDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), day);
    
    this.selectedDate = selectedDate;
    this.value = this.formatDateForInput(selectedDate);
    this.onChange(this.value);
    this.dateChange.emit(this.value);
    
    // Cerrar automáticamente el calendario después de seleccionar
    this.closeCalendar();
  }

  // Formatear fecha para input (YYYY-MM-DD)
  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Formatear fecha para mostrar (DD/MM/YYYY)
  formatDateForDisplay(): string {
    if (!this.selectedDate) return '';
    
    const day = String(this.selectedDate.getDate()).padStart(2, '0');
    const month = String(this.selectedDate.getMonth() + 1).padStart(2, '0');
    const year = this.selectedDate.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // Obtener mes y año actual
  getCurrentMonthYear(): string {
    return `${this.months[this.currentDate.getMonth()]} ${this.currentDate.getFullYear()}`;
  }
}
